import IdTokenVerifier from "idtoken-verifier";
import { DecodedJwt, IAuth0Config } from "../interfaces";

export const decodeJwt = (jwt: string): DecodedJwt => {
  try {
    const header = JSON.parse(atob(jwt.split(".")[0]));
    const payload = JSON.parse(atob(jwt.split(".")[1]));
    return {
      header,
      payload,
    };
  } catch (e) {
    throw new Error("Could not decode jwt");
  }
};

class MemoryJwksCache {
  private store = new Map<string, unknown>();
  has(key: string) {
    return this.store.has(key);
  }
  get(key: string) {
    return this.store.get(key);
  }
  set(key: string, value: unknown) {
    this.store.set(key, value);
    return value;
  }
}

const verifierCache = new Map<string, IdTokenVerifier>();

// idtoken-verifier is built for OIDC ID tokens: when `aud` has more than one
// value it requires `azp` to equal the configured audience. Real Auth0
// *access* tokens legitimately have both a custom API audience and the
// userinfo URL in `aud` (whenever `openid` scope is requested), with `azp`
// set to the client ID instead - so its own verify() rejects every real
// access token. We only borrow its JWKS fetching/caching and raw RSA
// signature check (getRsaVerifier), and do our own access-token-appropriate
// claim checks (below) instead of its ID-token claim orchestration.
const getVerifier = (auth0Config: IAuth0Config): IdTokenVerifier => {
  const cacheKey = `${auth0Config.customDomain}::${auth0Config.audience}`;
  const cached = verifierCache.get(cacheKey);
  if (cached) return cached;
  const verifier = new IdTokenVerifier({
    issuer: `https://${auth0Config.customDomain}/`,
    audience: auth0Config.audience,
    jwksURI: `https://${auth0Config.customDomain}/.well-known/jwks.json`,
    jwksCache: new MemoryJwksCache(),
  });
  verifierCache.set(cacheKey, verifier);
  return verifier;
};

function base64UrlToHex(base64Url: string): string {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  let hex = "";
  for (let i = 0; i < binary.length; i++) {
    hex += binary.charCodeAt(i).toString(16).padStart(2, "0");
  }
  return hex;
}

interface RsaVerifier {
  verify(data: string, signature: string): boolean;
}

type GetRsaVerifier = (
  iss: string,
  kid: string,
  cb: (err: Error | null, rsaVerifier?: RsaVerifier) => void
) => void;

/**
 * Validates claim shape/values only - no cryptographic check. Mirrors
 * OIDC access-token requirements, not idtoken-verifier's ID-token rules
 * (see getVerifier above for why we don't use its own claim checks).
 */
function assertClaimsAreValid(
  decoded: DecodedJwt,
  auth0Config: IAuth0Config
): void {
  if (decoded.header.alg !== "RS256") {
    throw new Error(
      `Signature algorithm of "${decoded.header.alg}" is not supported. Expected the token to be signed with "RS256".`
    );
  }
  if (!decoded.payload.sub) {
    throw new Error(
      "Subject (sub) claim must be a string present in the token"
    );
  }
  const audiences = Array.isArray(decoded.payload.aud)
    ? decoded.payload.aud
    : [decoded.payload.aud];
  if (!audiences.includes(auth0Config.audience)) {
    throw new Error(
      `Audience (aud) claim mismatch in the token; expected "${auth0Config.audience}" but found "${decoded.payload.aud}"`
    );
  }
  if (isNaN(decoded.payload.exp)) {
    throw new Error(
      "Expiration Time (exp) claim must be a number present in the token"
    );
  }
  if (isNaN(decoded.payload.iat)) {
    throw new Error(
      "Issued At (iat) claim must be a number present in the token"
    );
  }

  const leeway = 60;
  const now = new Date(Date.now());
  const expDate = new Date(0);
  expDate.setUTCSeconds(decoded.payload.exp + leeway);
  if (now > expDate) {
    throw new Error(
      `Expiration Time (exp) claim error in the token; the token is expired`
    );
  }

  const expectedIssuer = `https://${auth0Config.customDomain}/`;
  if (decoded.payload.iss !== expectedIssuer) {
    throw new Error(
      `Issuer (iss) claim mismatch in the token; expected "${expectedIssuer}" but found "${decoded.payload.iss}"`
    );
  }
}

/**
 * Cryptographic check only, via idtoken-verifier's JWKS-backed RSA
 * primitive - assertClaimsAreValid has already run by the time this
 * is called from verifyJwt.
 */
function verifySignature(
  jwt: string,
  decoded: DecodedJwt,
  auth0Config: IAuth0Config
): Promise<void> {
  const verifier = getVerifier(auth0Config) as unknown as {
    getRsaVerifier: GetRsaVerifier;
  };
  const [header, payload, signature] = jwt.split(".");
  const signatureHex = base64UrlToHex(signature);

  return new Promise<void>((resolve, reject) => {
    verifier.getRsaVerifier(
      decoded.payload.iss,
      decoded.header.kid,
      (err, rsaVerifier) => {
        if (err) {
          reject(
            new Error(`Jwt signature verification failed: ${err.message}`)
          );
          return;
        }
        if (!rsaVerifier!.verify(`${header}.${payload}`, signatureHex)) {
          reject(
            new Error("Jwt signature verification failed: Invalid signature")
          );
          return;
        }
        resolve();
      }
    );
  });
}

export const verifyJwt = async (
  jwt: string,
  decoded: DecodedJwt,
  auth0Config: IAuth0Config
): Promise<void> => {
  assertClaimsAreValid(decoded, auth0Config);
  await verifySignature(jwt, decoded, auth0Config);
};
